import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  SectionContextProvider,
  EditableToken,
} from '@vibesites/runtime/react';
import { useColor4AsPrimaryVars } from '../hooks/useCtaColorVars';

// ============================================================================
// Component
// ============================================================================

export function ContactForm() {
  const prefix = 'contactform';
  const formBlockPath = `${prefix}.form_block`;

  const formSlot = useBlockSlot(formBlockPath, {
    defaultValue: 'multihub-lead-form',
  });
  const color4Vars = useColor4AsPrimaryVars();

  return (
    <>
      {/* ── White background + centered lead form ── */}
      <div className="cf-parallax-section">
        {/* Centered form */}
        <div className="cf-parallax-content">
          <div className="cf-form-wrap" style={color4Vars}>
            <EditableToken path={formBlockPath} as="div">
              {formSlot ? (
                <SectionContextProvider sectionId={formSlot.sectionId}>
                  <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                </SectionContextProvider>
              ) : (
                <div className="cf-form-empty-card">
                  <p className="cf-form-empty">No form block selected</p>
                </div>
              )}
            </EditableToken>
          </div>
        </div>
      </div>

      {/* Schema-lint anchor — static path so the linter can match the schema key.
          The live EditableToken above uses a dynamic path from prefix. */}
      <EditableToken path="contactform.form_block" as="div" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>

      <style>{`
        /* ── Form section (plain white background) ── */
        .cf-parallax-section {
          position: relative;
          overflow: hidden;
          min-height: 0;
          padding: 1.25rem 1.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          background: white;
        }
        @media (min-width: 768px) {
          .cf-parallax-section {
            min-height: 520px;
            padding: 6rem 2rem;
          }
        }

        .cf-parallax-content {
          position: relative;
          z-index: 10;
          width: 100%;
          display: flex;
          justify-content: center;
        }

        .cf-form-wrap {
          width: 100%;
          max-width: 640px;
          border-radius: var(--vibe-radius-lg, 12px);
          overflow: hidden;
          box-shadow: 0 8px 40px rgba(0, 0, 0, 0.25);
        }
        /* Site typography: Space Mono body, Nunito Sans titles (loaded by the
           Header via the css2 link) and uppercase Oswald buttons. The block
           uses inline styles, so these overrides need !important. */
        .cf-form-wrap,
        .cf-form-wrap input,
        .cf-form-wrap select,
        .cf-form-wrap textarea {
          font-family: var(--vibe-font-body) !important;
        }
        .cf-form-wrap h3,
        .cf-form-wrap h3 * {
          font-family: 'Nunito Sans', sans-serif !important;
          font-weight: 400 !important;
          text-transform: uppercase;
          letter-spacing: 0.02em;
        }
        .cf-form-wrap .mh-lead-form-submit-button,
        .cf-form-wrap .mh-lead-form-success-cta {
          font-family: var(--vibe-font-heading) !important;
          font-weight: 400 !important;
          font-size: 0.875rem !important;
          text-transform: uppercase;
          letter-spacing: 0.1em;
        }
        .cf-form-empty-card {
          background: white;
          border-radius: var(--vibe-radius-lg, 12px);
          padding: 2rem;
        }
        .cf-form-empty {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
          text-align: center;
          margin: 0;
          padding: 2rem 0;
        }
      `}</style>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ContactForm',
  displayName: 'Contact Form',
  tokens: {
    'contactform.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-lead-form',
      acceptedBlockCategories: ['Forms'],
    },
  },
};

(ContactForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ContactForm;
